import React, { useEffect, useMemo, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import { useLots } from '../../hooks/useLots'
import { useDeletedLotRowMatcher } from '../../hooks/useDeletedLotRowMatcher'
import type { WizardFarm, WizardProductionUnit } from '../../utils/farmWizard'
import { saveEditedFarm } from '../../utils/editFarmApi'
import { listProductionUnitTableRows, type BiomassDataRow } from '../../utils/productionUnitTable'
import { computeCurrentBiomassPerLot } from '../../utils/currentBiomassPerLot'
import { mortalityLogApi } from '../../utils/mortalityLogApi'
import { weightSampleLogApi } from '../../utils/weightSampleLogApi'
import { fishStockLogApi } from '../../utils/fishStockLogApi'
import { feedLogApi } from '../../utils/feedLogApi'
import { loadWaterTableRows, refreshFarmWaterRowsFromApi } from '../../utils/waterTable'
import { pickReading, resolveMeasuringPoint, type PointReading } from '../../utils/measuringPoint'
import { readFlowLpm, isHatcheryLot } from '../../utils/hatchery'
import { farmDayKey, farmDayOffset, farmTimeZone } from '../../utils/farmTime'
import { isInactive } from '../../utils/unitStatus'
import { isLotActiveForInteraction } from '../../utils/lotInteraction'
import { feedPlanMode, feedPlanTotalKg, readFeedPlan, withFeedPlanTotalKg, withUnitPlans } from '../../utils/unitPlans'
import { readDensityLimits } from '../../utils/density'
import { checkFeedStop, latestOxygenAndFlow, latestReadingsOfDay, oxygenFeedCapacityKg, readFeedRules, SUGGESTED_OUTLET_DO_MIN_MG_L, type LatestValue } from '../../utils/feedRules'
import { crewTransferState, readPlannedTransfer, readTransferRation } from '../../utils/transferProtocol'
import { CAPACITY_STATUS_COLOR, capacityStatusWordKey, resolveLotCapacity, statusAgainstMaximum, statusAgainstMinimum } from '../../utils/carryingCapacity'
import type { MatrixData } from '../../components/MatrixTable'
import { getDefaultMatrixForSpecies, interpolateFromMatrix, loadCachedMatrix, resolveSfrMatrixPresetKey } from '../../utils/sfrMatrix'
import { loadSfrMatrix } from '../../utils/sfrMatrixApi'
import { resolveEffectiveSfrMatrix } from '../../utils/sfrProfiles'
import { resolveFarmProductionSpeciesKey } from '../../utils/farmProductionSpecies'
import { loadStoredSpecies } from '../../data/productionSpeciesPresets'
import {
  STALE_TEMPERATURE_DAYS,
  STALE_WEIGHT_DAYS,
  buildFarmFeedRows,
  daysOld,
  lotsInSeveralUnits,
  roundPlanKg,
  totalsOf,
  type FarmFeedLotInput,
  type FarmFeedRow,
  type FarmFeedUnitInput
} from './farmFeedRows'

// Full-farm feed table (findings #101): the office sets each unit's plan from
// the recommended ration. Crew Mode keeps reading the unit plan ("Dado según plan").

type LoadedData = {
  rowsByUnitId: Map<string, BiomassDataRow[]>
  mortalityLogs: Awaited<ReturnType<typeof mortalityLogApi.list>>
  weightSampleLogs: Awaited<ReturnType<typeof weightSampleLogApi.list>>
  fishStockLogs: Awaited<ReturnType<typeof fishStockLogApi.list>>
  feedLogs: Awaited<ReturnType<typeof feedLogApi.list>>
}

const fmt = (value: number | null, digits = 1): string => (value === null ? '—' : value.toFixed(digits))
const fmtInt = (value: number | null): string => (value === null ? '—' : Math.round(value).toLocaleString())

const latestReading = <T,>(rows: T[], pick: (row: T) => number | null, dateOf: (row: T) => string): { date: string; value: number } | null => {
  let best: { date: string; value: number } | null = null
  for (const row of rows) {
    const value = pick(row)
    if (value === null || !Number.isFinite(value)) continue
    const date = dateOf(row)
    if (!best || date > best.date) best = { date, value }
  }
  return best
}

export type FarmFeedTableProps = {
  farm: WizardFarm
  canEdit: boolean
  onFarmSaved: (farm: WizardFarm) => void
}

export default function FarmFeedTable({ farm, canEdit, onFarmSaved }: FarmFeedTableProps) {
  const t = useT()
  const farmId = String(farm.id ?? '')
  const timeZone = farmTimeZone(farm)
  const units = useMemo(
    () => ((farm.productionUnits ?? []) as WizardProductionUnit[]).filter((unit) => !isInactive(unit)),
    [farm.productionUnits]
  )
  const { lots } = useLots({ farmId, enabled: Boolean(farmId) })
  const activeLots = useMemo(() => lots.filter((lot) => isLotActiveForInteraction(lot.stage)), [lots])
  // Findings #207: a deleted lot's records never count for an active lot with the same name.
  const deletedLotFarmIds = useMemo(() => (farmId ? [farmId] : []), [farmId])
  const isDeletedLotRow = useDeletedLotRowMatcher(deletedLotFarmIds)

  const [data, setData] = useState<LoadedData | null>(null)
  const [loading, setLoading] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const speciesKey = resolveFarmProductionSpeciesKey(farm) ?? 'global'
  const profileSpeciesKey = resolveFarmProductionSpeciesKey(farm) ?? loadStoredSpecies()
  const [speciesMatrix, setSpeciesMatrix] = useState<MatrixData>(() => (
    loadCachedMatrix('global') ?? getDefaultMatrixForSpecies('global')
  ))

  useEffect(() => {
    let cancelled = false
    void loadSfrMatrix(resolveSfrMatrixPresetKey(speciesKey), { preferRecentLocal: true }).then((result) => {
      if (!cancelled) setSpeciesMatrix(result.matrix)
    })
    return () => {
      cancelled = true
    }
  }, [speciesKey])

  const unitIdsKey = units.map((unit) => unit.id).join('|')
  useEffect(() => {
    if (!farmId) return
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const [mortalityLogs, weightSampleLogs, fishStockLogs, feedLogs, unitRows] = await Promise.all([
          mortalityLogApi.list({ farmId }).catch(() => []),
          weightSampleLogApi.list({ farmId }).catch(() => []),
          fishStockLogApi.list({ farmId }).catch(() => []),
          feedLogApi.list({ farmId }).catch(() => []),
          Promise.all(units.map(async (unit) => {
            const rows = await listProductionUnitTableRows({
              farmId,
              productionUnitId: unit.id,
              farmName: farm.name,
              productionUnitName: unit.name
            }).catch(() => [] as BiomassDataRow[])
            return [unit.id, rows] as const
          })),
          // Findings #217: readings taken on the crew's phones (last 30 days), before the table reads them.
          refreshFarmWaterRowsFromApi({
            farmId,
            farmName: farm.name,
            units: (farm.productionUnits ?? []) as WizardProductionUnit[],
            fromRecordedAt: new Date(Date.now() - 30 * 86_400_000).toISOString()
          }).catch(() => undefined)
        ])
        if (cancelled) return
        setData({ rowsByUnitId: new Map(unitRows), mortalityLogs, weightSampleLogs, fishStockLogs, feedLogs })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, unitIdsKey])

  const densityLimits = useMemo(() => readDensityLimits(farm), [farm])

  // Findings #217: each unit's readings, or its measuring point's when the unit has
  // none today (own today, then the point's today, then the newer of the two).
  const waterByUnit = useMemo(() => {
    const today = farmDayOffset(0, timeZone)
    const allUnits = (farm.productionUnits ?? []) as WizardProductionUnit[]
    const rowsOf = (unit: WizardProductionUnit) => loadWaterTableRows({ farmId, productionUnitId: unit.id, farmName: farm.name, productionUnitName: unit.name })
    const temperatureOf = (rows: ReturnType<typeof loadWaterTableRows>): LatestValue | null => {
      const latest = latestReading(rows, (row) => (typeof row.temperature === 'number' ? row.temperature : null), (row) => String(row.date ?? ''))
      return latest ? { value: latest.value, at: latest.date } : null
    }
    const out = new Map<string, {
      ownRows: ReturnType<typeof loadWaterTableRows>
      temperature: PointReading | null
      dissolvedOxygen: PointReading | null
      today: { temperature: PointReading | null; dissolvedOxygen: PointReading | null }
    }>()
    for (const unit of units) {
      const ownRows = rowsOf(unit)
      const point = resolveMeasuringPoint(unit, allUnits)
      const pointRows = point ? rowsOf(point) : []
      const pick = (own: LatestValue | null, fromPoint: LatestValue | null) => pickReading({ own, point: fromPoint, pointName: point?.name, day: today, timeZone })
      const ownToday = latestReadingsOfDay(ownRows, today, timeZone)
      const pointToday = latestReadingsOfDay(pointRows, today, timeZone)
      out.set(unit.id, {
        ownRows,
        temperature: pick(temperatureOf(ownRows), temperatureOf(pointRows)),
        dissolvedOxygen: pick(latestOxygenAndFlow(ownRows).dissolvedOxygen, latestOxygenAndFlow(pointRows).dissolvedOxygen),
        today: {
          temperature: pick(ownToday.temperature, pointToday.temperature),
          dissolvedOxygen: pick(ownToday.dissolvedOxygen, pointToday.dissolvedOxygen)
        }
      })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, farm.productionUnits, farmId, farm.name, timeZone, data])

  const rows: FarmFeedRow[] = useMemo(() => {
    if (!data) return []
    const yesterday = farmDayOffset(-1, timeZone)
    const today = farmDayOffset(0, timeZone)
    const inputs: FarmFeedUnitInput[] = units.map((unit) => {
      const unitLots = activeLots.filter((lot) => lot.productionUnitId === unit.id)
      const byUnit = <L extends { productionUnitId?: string }>(logs: L[]) => logs.filter((log) => log.productionUnitId === unit.id && !isDeletedLotRow(log))
      const current = computeCurrentBiomassPerLot({
        productionRows: data.rowsByUnitId.get(unit.id) ?? [],
        mortalityLogs: byUnit(data.mortalityLogs),
        weightSampleLogs: byUnit(data.weightSampleLogs),
        fishStockLogs: byUnit(data.fishStockLogs)
      })
      const lotInputs: FarmFeedLotInput[] = unitLots.map((lot) => {
        const entry = current.get(lot.id)
        return {
          id: lot.id,
          lotKey: lot.lotKey || lot.id,
          preFeeding: isHatcheryLot(lot),
          lineageRootId: lot.lineageRootId ?? null,
          fishCount: entry?.sourceStockCount ?? null,
          averageWeightG: entry?.sourceWeightG ?? null,
          biomassKg: entry?.biomassKg ?? null,
          asOfDate: entry?.asOfDate ?? null
        }
      })
      const unitWater = waterByUnit.get(unit.id)
      const water = unitWater?.ownRows ?? []
      const fedOn = (day: string) => byUnit(data.feedLogs)
        .filter((log) => farmDayKey(log.recordedAt, timeZone) === day)
        .reduce((total, log) => total + (Number.parseFloat(String(log.feedKg ?? '')) || 0), 0)
      const fedYesterday = fedOn(yesterday)
      const fedToday = fedOn(today)
      const plan = readFeedPlan(unit)
      const temperature = unitWater?.temperature ?? null
      return {
        id: unit.id,
        name: unit.name,
        type: unit.type,
        capacity: unit.capacity,
        capacityUnit: unit.capacityUnit,
        lots: lotInputs,
        temperatureC: temperature?.value ?? null,
        temperatureDate: temperature?.at ?? null,
        flowLpm: latestReading(water, (row) => readFlowLpm(row), (row) => String(row.date ?? ''))?.value ?? null,
        // Findings #182: the plan's total over its feeds; tomas only when the crew records per toma.
        planKgPerDay: plan ? feedPlanTotalKg(plan) : null,
        planFeedingsPerDay: plan && feedPlanMode(plan) === 'perFeeding' ? plan.feedingsPerDay : null,
        fedYesterdayKg: fedYesterday > 0 ? fedYesterday : null,
        fedTodayKg: fedToday > 0 ? fedToday : null
      }
    })
    const lotById = new Map(activeLots.map((lot) => [lot.id, lot]))
    const unitById = new Map(units.map((unit) => [unit.id, unit]))
    const lotUnit = new Map(activeLots.map((lot) => [lot.id, lot.productionUnitId ?? '']))
    const capacityLotsById = new Map(activeLots.map((lot) => [lot.id, lot]))
    return buildFarmFeedRows(inputs, {
      densityLimits,
      capacityForLot: (lotId) => resolveLotCapacity(farm, capacityLotsById.get(lotId), capacityLotsById)?.table ?? null,
      sfrFor: (lotId, weightG, temperatureC) => {
        const matrix = resolveEffectiveSfrMatrix({
          farm,
          productionUnit: unitById.get(lotUnit.get(lotId) ?? ''),
          lot: lotById.get(lotId),
          species: profileSpeciesKey,
          speciesDefaultMatrix: speciesMatrix
        })
        const result = interpolateFromMatrix(matrix, weightG, temperatureC, { species: speciesKey })
        return Number.isFinite(result.sfr) && result.sfr > 0 ? result.sfr : null
      }
    })
  }, [data, units, activeLots, isDeletedLotRow, farm, farmId, timeZone, densityLimits, speciesMatrix, profileSpeciesKey, speciesKey, waterByUnit])

  // Findings #211: feed the unit's water can carry, and units the crew must not feed today.
  // Findings #210: lots fasting before a planned move or on reduced ration after one.
  const oxygenByUnit = useMemo(() => {
    const today = farmDayOffset(0, timeZone)
    const out = new Map<string, { capacityKg: number | null; stoppedToday: boolean; transfer: ReturnType<typeof crewTransferState>[] }>()
    for (const unit of units) {
      const unitWater = waterByUnit.get(unit.id)
      const rules = readFeedRules(unit)
      // Caudal is the unit's own; the inlet oxygen may come from its measuring point (#217).
      const latest = latestOxygenAndFlow(unitWater?.ownRows ?? [])
      const capacityKg = oxygenFeedCapacityKg({
        flowLpm: latest.flowLpm?.value ?? null,
        inletDoMgL: unitWater?.dissolvedOxygen?.value ?? null,
        outletDoMinMgL: rules?.outletDoMinMgL ?? SUGGESTED_OUTLET_DO_MIN_MG_L,
        o2KgPerKgFeed: rules?.o2KgPerKgFeed
      })
      const stoppedToday = checkFeedStop(rules, unitWater?.today ?? { temperature: null, dissolvedOxygen: null }).reasons.length > 0
      const transfer = activeLots
        .filter((lot) => lot.productionUnitId === unit.id)
        .map((lot) => crewTransferState({ planned: readPlannedTransfer(lot), ration: readTransferRation(lot), productionUnitId: unit.id, today, backToNormal: false }))
        .filter((state) => state !== null)
      out.set(unit.id, { capacityKg, stoppedToday, transfer })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, activeLots, timeZone, waterByUnit])

  const sharedLots = useMemo(() => lotsInSeveralUnits(rows), [rows])
  const totals = useMemo(() => totalsOf(rows), [rows])

  const applyRecommended = async (unitIds: string[]) => {
    const recommendedByUnit = new Map(rows.map((row) => [row.unitId, row.recommendedKgPerDay]))
    // Findings #162: the notice names each unit with its new and previous kg/day.
    const changes: string[] = []
    const nextUnits = (farm.productionUnits ?? []).map((unit) => {
      if (!unitIds.includes(unit.id)) return unit
      const plan = readFeedPlan(unit)
      const recommended = recommendedByUnit.get(unit.id)
      if (!plan || recommended === null || recommended === undefined) return unit
      const kgPerDay = roundPlanKg(recommended)
      // Findings #166: only units whose plan really changes are listed (and saved).
      const before = feedPlanTotalKg(plan)
      if (fmt(kgPerDay) === fmt(before)) return unit
      changes.push(t('feed.farmTable.planChange', { unit: unit.name, kg: fmt(kgPerDay), before: fmt(before) }))
      // Findings #182: with two feeds, each keeps its share of the new total.
      return withUnitPlans(unit, { feedPlan: withFeedPlanTotalKg(plan, kgPerDay) })
    })
    if (changes.length === 0) {
      setNotice({ tone: 'success', text: t('feed.farmTable.planAlreadyRecommended') })
      return
    }
    const nextFarm: WizardFarm = { ...farm, productionUnits: nextUnits }
    setSaving(true)
    setNotice(null)
    try {
      await saveEditedFarm(nextFarm, farm)
      onFarmSaved(nextFarm)
      setNotice({ tone: 'success', text: t('feed.farmTable.planUpdated', { changes: changes.join(' · ') }) })
    } catch {
      setNotice({ tone: 'error', text: t('feed.farmTable.planSaveError') })
    } finally {
      setSaving(false)
    }
  }

  const planDiffers = (row: FarmFeedRow): boolean =>
    row.recommendedKgPerDay !== null && (row.planKgPerDay === null || Math.abs(roundPlanKg(row.recommendedKgPerDay) - row.planKgPerDay) >= 0.05)
  const hasFeedPlan = (unitId: string): boolean => Boolean(readFeedPlan(units.find((unit) => unit.id === unitId)))

  // findings #113: the day each value comes from, orange when old.
  const today = farmDayOffset(0, timeZone)
  const renderAge = (day: string | null, staleAfter: number) => {
    const age = daysOld(day, today)
    if (day === null || age === null) return null
    const stale = age > staleAfter
    return (
      <span style={{ display: 'block', fontSize: 11, color: stale ? '#c2410c' : '#64748b', fontWeight: stale ? 700 : 400 }} title={stale ? t('feed.farmTable.staleTitle', { days: age }) : undefined}>
        {age === 0 ? t('feed.farmTable.today') : t('feed.farmTable.daysAgo', { days: age })}
      </span>
    )
  }

  const cell: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'right' }
  const head: React.CSSProperties = { ...cell, fontWeight: 700, background: '#f8fafc', fontSize: 12 }

  return (
    <div data-testid="farm-feed-table" style={{ display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 13, color: '#475569' }}>{t('feed.farmTable.intro')}</div>
      {notice ? (
        <div role="status" style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, background: notice.tone === 'success' ? '#ecfdf5' : '#fef2f2', color: notice.tone === 'success' ? '#065f46' : '#991b1b' }}>
          {notice.text}
        </div>
      ) : null}
      {loading && !data ? <div style={{ fontSize: 13, color: '#64748b' }}>{t('feed.farmTable.loading')}</div> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>{t('feed.farmTable.col.unit')}</th>
              <th style={{ ...head, textAlign: 'left' }}>{t('feed.farmTable.col.lot')}</th>
              <th style={head}>{t('feed.farmTable.col.fish')}</th>
              <th style={head}>{t('feed.farmTable.col.weight')}</th>
              <th style={head}>{t('feed.farmTable.col.biomass')}</th>
              <th style={head}>{t('feed.farmTable.col.temperature')}</th>
              <th style={head}>{t('feed.farmTable.col.sfr')}</th>
              <th style={head}>{t('feed.farmTable.col.recommended')}</th>
              <th style={head}>{t('feed.farmTable.col.plan')}</th>
              <th style={head}>{t('feed.farmTable.col.fedYesterday')}</th>
              <th style={head}>{t('feed.farmTable.col.fedToday')}</th>
              <th style={head}>{t('feed.farmTable.col.density')}</th>
              <th style={head}>{t('feed.farmTable.col.flowPerKg')}</th>
              <th style={head} title={t('feed.farmTable.oxygenHelp')}>{t('feed.farmTable.col.oxygenCapacity')}</th>
              {canEdit ? <th style={head} /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              // Findings #151: green / amber / red against the lot's Capacidad de carga table.
              const densityStatus = statusAgainstMaximum(row.densityKgM3, row.densityLimitKgM3)
              const flowStatus = statusAgainstMinimum(row.flowLpmPerKg, row.minFlowLpmPerKg)
              const densityWord = capacityStatusWordKey(densityStatus, 'maximum')
              const flowWord = capacityStatusWordKey(flowStatus, 'minimum')
              const tableSuffix = row.densityTableName ? ` (${row.densityTableName})` : ''
              const onlyEggs = row.lots.length > 0 && row.lots.every((lot) => lot.preFeeding)
              const oxygen = oxygenByUnit.get(row.unitId)
              const overOxygen = oxygen?.capacityKg !== null && oxygen?.capacityKg !== undefined && row.planKgPerDay !== null && row.planKgPerDay > oxygen.capacityKg
              return (
                <tr key={row.unitId} data-testid={`farm-feed-row-${row.unitId}`}>
                  <td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>
                    {row.unitName}
                    {oxygen?.stoppedToday ? (
                      <span data-testid={`farm-feed-stopped-${row.unitId}`} title={t('feed.farmTable.stoppedTitle')} style={{ marginLeft: 6, color: '#b91c1c', fontWeight: 700 }}>
                        ⛔ {t('feed.farmTable.stoppedToday')}
                      </span>
                    ) : null}
                    {oxygen?.transfer.map((state, index) => state ? (
                      <span key={index} data-testid={`farm-feed-transfer-${row.unitId}`} style={{ display: 'block', fontSize: 11, color: '#b45309', fontWeight: 700 }}>
                        {state.kind === 'fasting'
                          ? t('feed.farmTable.fastingToday')
                          : t('feed.farmTable.reducedToday', { day: state.day, of: state.of, kg: fmt(state.kgPerDay, 3) })}
                      </span>
                    ) : null)}
                  </td>
                  <td style={{ ...cell, textAlign: 'left' }}>
                    {row.lots.length === 0
                      ? <span style={{ color: '#94a3b8' }}>{t('feed.farmTable.noLot')}</span>
                      : row.lots.map((lot) => (lot.preFeeding ? `🥚 ${lot.lotKey}` : lot.lotKey)).join(', ')}
                  </td>
                  <td style={cell}>
                    {row.fishCount === null && row.eggCount !== null
                      ? fmtInt(row.eggCount)
                      : fmtInt(row.fishCount)}
                  </td>
                  <td style={cell}>{fmt(row.averageWeightG)}{renderAge(row.averageWeightG === null ? null : row.weightDate, STALE_WEIGHT_DAYS)}</td>
                  <td style={cell}>{fmt(row.biomassKg)}</td>
                  <td style={cell}>
                    {fmt(row.temperatureC)}{renderAge(row.temperatureC === null ? null : row.temperatureDate, STALE_TEMPERATURE_DAYS)}
                    {row.temperatureC !== null && waterByUnit.get(row.unitId)?.temperature?.fromUnit ? (
                      <span data-testid={`farm-feed-temperature-from-${row.unitId}`} style={{ color: '#64748b', fontSize: 11 }}>
                        {' '}{t('feed.farmTable.measuredIn', { unit: waterByUnit.get(row.unitId)?.temperature?.fromUnit ?? '' })}
                      </span>
                    ) : null}
                  </td>
                  <td style={cell}>{fmt(row.sfrPercent, 2)}</td>
                  <td style={{ ...cell, fontWeight: 700 }}>
                    {onlyEggs
                      ? <span style={{ color: '#94a3b8', fontWeight: 400 }} title={t('feed.farmTable.eggsNoRation')} data-testid={`farm-feed-eggs-${row.unitId}`}>{t('feed.farmTable.eggsDash')}</span>
                      : fmt(row.recommendedKgPerDay)}
                  </td>
                  <td style={cell}>
                    {fmt(row.planKgPerDay)}
                    {row.planKgPerDay !== null && row.planFeedingsPerDay
                      ? <span style={{ color: '#64748b' }}>{` · ${t('feed.farmTable.feedings', { count: row.planFeedingsPerDay })}`}</span>
                      : null}
                  </td>
                  <td style={cell}>{fmt(row.fedYesterdayKg)}</td>
                  <td style={cell} data-testid={`farm-feed-fed-today-${row.unitId}`}>{fmt(row.fedTodayKg)}</td>
                  <td
                    style={{ ...cell, color: densityStatus && densityStatus !== 'ok' ? CAPACITY_STATUS_COLOR[densityStatus] : undefined, fontWeight: densityStatus && densityStatus !== 'ok' ? 700 : undefined }}
                    title={densityStatus === 'red'
                      ? `${t('density.overLimitTitle')}${tableSuffix}`
                      : densityStatus === 'amber'
                        ? `${t('density.nearLimitTitle')}${tableSuffix}`
                        : row.densityLimitKgM3 !== null ? `${t('density.limitLabel', { limit: fmt(row.densityLimitKgM3) })}${tableSuffix}`
                          : row.densityKgM3 === null && row.biomassKg !== null ? t('density.noVolume') : undefined}
                    data-testid={`farm-feed-density-${row.unitId}`}
                    data-status={densityStatus ?? undefined}
                  >
                    {fmt(row.densityKgM3)}
                    {densityStatus && densityStatus !== 'ok' ? ` · ${t('density.limitLabel', { limit: fmt(row.densityLimitKgM3) })}` : ''}
                    {densityWord ? ` · ${t(densityWord)}` : ''}
                  </td>
                  <td
                    style={{ ...cell, color: flowStatus && flowStatus !== 'ok' ? CAPACITY_STATUS_COLOR[flowStatus] : undefined, fontWeight: flowStatus && flowStatus !== 'ok' ? 700 : undefined }}
                    title={row.minFlowLpmPerKg !== null ? `${t('capacity.flow.minimumLabel', { minimum: fmt(row.minFlowLpmPerKg, 2) })}${tableSuffix}` : undefined}
                    data-testid={`farm-feed-flow-${row.unitId}`}
                    data-status={flowStatus ?? undefined}
                  >
                    {fmt(row.flowLpmPerKg, 2)}
                    {flowStatus && flowStatus !== 'ok' ? ` · ${t('capacity.flow.minimumLabel', { minimum: fmt(row.minFlowLpmPerKg, 2) })}` : ''}
                    {flowWord ? ` · ${t(flowWord)}` : ''}
                  </td>
                  <td
                    style={{ ...cell, color: overOxygen ? '#b91c1c' : undefined, fontWeight: overOxygen ? 700 : undefined }}
                    title={overOxygen ? t('feed.farmTable.overOxygenTitle') : undefined}
                    data-testid={`farm-feed-oxygen-${row.unitId}`}
                  >
                    {fmt(oxygen?.capacityKg ?? null)}
                  </td>
                  {canEdit ? (
                    <td style={cell}>
                      {onlyEggs ? null : !hasFeedPlan(row.unitId) ? (
                        row.recommendedKgPerDay !== null
                          ? <span style={{ fontSize: 12, color: '#94a3b8' }} title={t('feed.farmTable.noPlanHelp')}>{t('feed.farmTable.noPlan')}</span>
                          : null
                      ) : planDiffers(row) ? (
                        <GradientButton
                          size="xs"
                          variant="info"
                          disabled={saving}
                          onClick={() => void applyRecommended([row.unitId])}
                          data-testid={`farm-feed-use-recommended-${row.unitId}`}
                        >
                          {t('feed.farmTable.useRecommended')}
                        </GradientButton>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...cell, textAlign: 'left', fontWeight: 700 }} colSpan={4}>{t('feed.farmTable.total')}</td>
              <td style={{ ...cell, fontWeight: 700 }}>{fmt(totals.biomassKg)}</td>
              <td style={cell} />
              <td style={cell} />
              <td style={{ ...cell, fontWeight: 700 }}>{fmt(totals.recommendedKgPerDay)}</td>
              <td style={{ ...cell, fontWeight: 700 }}>{fmt(totals.planKgPerDay)}</td>
              <td style={{ ...cell, fontWeight: 700 }}>{fmt(totals.fedYesterdayKg)}</td>
              <td style={{ ...cell, fontWeight: 700 }}>{fmt(totals.fedTodayKg)}</td>
              <td style={cell} colSpan={canEdit ? 4 : 3} />
            </tr>
          </tfoot>
        </table>
      </div>
      {canEdit && sharedLots.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {sharedLots.map((shared) => (
            <GradientButton
              key={shared.lotId}
              size="xs"
              variant="secondary"
              disabled={saving}
              onClick={() => void applyRecommended(shared.unitIds)}
              data-testid={`farm-feed-use-recommended-lot-${shared.lotId}`}
            >
              {t('feed.farmTable.useRecommendedLot', { lot: shared.lotKey, count: shared.unitIds.length })}
            </GradientButton>
          ))}
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: '#64748b' }}>{t('feed.farmTable.help')}</div>
    </div>
  )
}
